import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { cruiseLabel } from '../utils/cruiseNames';
import { NAVY, GOLD } from '../utils/constants';

// ── Helpers ───────────────────────────────────────────────────────────────────
const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const getFinancialYear = (yyyymmdd) => {
  const [y, m] = (yyyymmdd || todayInput()).split('-').map(Number);
  return m >= 4
    ? `${y}-${String(y + 1).slice(-2)}`
    : `${y - 1}-${String(y).slice(-2)}`;
};

export const computeInvoiceTax = (booking, settings, cruiseSetting, partnerContext, pricingRates) => {
  if (!settings) return null;
  const adults   = Number(booking.adults)   || 0;
  const children = Number(booking.children) || 0;
  const isDinner = (booking.cruise_type || '').toLowerCase().includes('dinner');

  // B2B (partner) invoice: requires a GSTIN and a positive TAC rate. Anything
  // else — B2C, or a partner without GSTIN — falls through to the existing B2C
  // computation unchanged.
  const isB2B = !!(partnerContext && partnerContext.gstin && Number(partnerContext.tacRate) > 0);

  // Amount-driven discount: the ACTUAL stored booking amounts are the single
  // source of truth for the discount — NOT tac_rate, which staff can override by
  // setting a custom tac_amount (e.g. 0 = no discount). effectiveFactor is the
  // "keep" ratio net/total: 0.9 for a standard 10% TAC, 1.0 when staff zeroed
  // the discount, and the exact ratio for any custom amount. It reconciles
  // perfectly: full * effectiveFactor = net_total.
  const bookingTotal    = Number(booking.total_amount) || 0;
  const bookingNet      = Number(booking.net_total)    || 0;
  const bookingTacAmt   = Number(booking.tac_amount)   || 0;
  const effectiveFactor = bookingTotal > 0 ? bookingNet / bookingTotal : 1;
  // Only show the discount block when there's an actual discount applied.
  const hasDiscount     = isB2B && bookingTacAmt > 0 && effectiveFactor < 1;

  if (isDinner) {
    const src = cruiseSetting || {};
    const adultCruise = Number(src.dinner_adult_cruise_charge) || 0;
    const adultFood   = Number(src.dinner_adult_food_charge)   || 0;
    const childCruise = Number(src.dinner_child_cruise_charge) || 0;
    const childFood   = Number(src.dinner_child_food_charge)   || 0;

    const cruiseRate    = Number(settings.cruise_gst_rate) || 18;
    const foodRate      = Number(settings.food_gst_rate)   || 5;
    const cgstCruisePct = cruiseRate / 2;
    const cgstFoodPct   = foodRate   / 2;

    if (isB2B) {
      // Discount-first: apply the actual (amount-driven) discount to EACH
      // per-head component BEFORE the GST back-calc, using the same GST rates.
      const rate   = Number(partnerContext.tacRate); // display rate for the label
      const factor = effectiveFactor;                // AMOUNT-driven, from real totals

      const discAdultCruise = adultCruise * factor;
      const discChildCruise = childCruise * factor;
      const discAdultFood   = adultFood   * factor;
      const discChildFood   = childFood   * factor;

      const adultCruiseTaxable = discAdultCruise / (1 + cruiseRate / 100);
      const childCruiseTaxable = discChildCruise / (1 + cruiseRate / 100);
      const adultFoodTaxable   = discAdultFood   / (1 + foodRate   / 100);
      const childFoodTaxable   = discChildFood   / (1 + foodRate   / 100);

      const taxableCruise = adults * adultCruiseTaxable + children * childCruiseTaxable;
      const cgstCruise    = taxableCruise * (cgstCruisePct / 100);
      const sgstCruise    = cgstCruise;

      const taxableFood = adults * adultFoodTaxable + children * childFoodTaxable;
      const cgstFood    = taxableFood * (cgstFoodPct / 100);
      const sgstFood    = cgstFood;

      const fullCruiseCharge = adults * adultCruise + children * childCruise;
      const fullFoodCharge   = adults * adultFood   + children * childFood;
      const fullGrandTotal   = fullCruiseCharge + fullFoodCharge;

      const discountedCruiseCharge = adults * discAdultCruise + children * discChildCruise;
      const discountedFoodCharge   = adults * discAdultFood   + children * discChildFood;
      const grandTotal = discountedCruiseCharge + discountedFoodCharge;

      // FULL (pre-discount) taxable base — back-calc on the full per-head charges,
      // for the item table's "Rate excl. GST" column (Option 1 display).
      const adultCruiseTaxableFull = adultCruise / (1 + cruiseRate / 100);
      const childCruiseTaxableFull = childCruise / (1 + cruiseRate / 100);
      const adultFoodTaxableFull   = adultFood   / (1 + foodRate   / 100);
      const childFoodTaxableFull   = childFood   / (1 + foodRate   / 100);
      const taxableCruiseFull = adults * adultCruiseTaxableFull + children * childCruiseTaxableFull;
      const taxableFoodFull   = adults * adultFoodTaxableFull   + children * childFoodTaxableFull;
      const taxableTotalFull  = taxableCruiseFull + taxableFoodFull;
      const discountTaxable   = taxableTotalFull - (taxableCruise + taxableFood);

      return {
        isDinner: true,
        adults, children,
        // per-head charges reported DISCOUNTED (line items show discounted rate)
        adultCruise: discAdultCruise, adultFood: discAdultFood,
        childCruise: discChildCruise, childFood: discChildFood,
        // full pre-discount per-head charges, for optional display
        adultCruiseFull: adultCruise, adultFoodFull: adultFood,
        childCruiseFull: childCruise, childFoodFull: childFood,
        adultCruiseTaxable, childCruiseTaxable,
        adultFoodTaxable,   childFoodTaxable,
        // full (pre-discount) per-head + summed taxable base
        adultCruiseTaxableFull, childCruiseTaxableFull,
        adultFoodTaxableFull,   childFoodTaxableFull,
        taxableCruiseFull, taxableFoodFull, taxableTotalFull,
        // DISCOUNTED per-head component taxables (line items show discounted rate)
        adultCruiseTaxableDisc: adultCruiseTaxable, childCruiseTaxableDisc: childCruiseTaxable,
        adultFoodTaxableDisc:   adultFoodTaxable,   childFoodTaxableDisc:   childFoodTaxable,
        showDiscountBlock: false,
        discountTaxable,
        cruiseRate, cgstCruisePct,
        foodRate,   cgstFoodPct,
        totalCruiseCharge: discountedCruiseCharge, totalFoodCharge: discountedFoodCharge,
        taxableCruise, cgstCruise, sgstCruise,
        taxableFood,   cgstFood,   sgstFood,
        taxableTotal: taxableCruise + taxableFood,
        totalTax:     cgstCruise + sgstCruise + cgstFood + sgstFood,
        grandTotal,
        isB2B: true,
        hasDiscount,
        tacRate: rate,
        fullGrandTotal,
        discountAmount: bookingTacAmt, // ACTUAL stored discount, not rate-derived
      };
    }

    const adultCruiseTaxable = adultCruise / (1 + cruiseRate / 100);
    const childCruiseTaxable = childCruise / (1 + cruiseRate / 100);
    const adultFoodTaxable   = adultFood   / (1 + foodRate   / 100);
    const childFoodTaxable   = childFood   / (1 + foodRate   / 100);

    const taxableCruise = adults * adultCruiseTaxable + children * childCruiseTaxable;
    const cgstCruise    = taxableCruise * (cgstCruisePct / 100);
    const sgstCruise    = cgstCruise;

    const taxableFood = adults * adultFoodTaxable + children * childFoodTaxable;
    const cgstFood    = taxableFood * (cgstFoodPct / 100);
    const sgstFood    = cgstFood;

    const totalCruiseCharge = adults * adultCruise + children * childCruise;
    const totalFoodCharge   = adults * adultFood   + children * childFood;

    return {
      isDinner: true,
      adults, children,
      adultCruise, adultFood, childCruise, childFood,
      adultCruiseTaxable, childCruiseTaxable,
      adultFoodTaxable,   childFoodTaxable,
      // B2C: *Disc component taxables = full (no discount)
      adultCruiseTaxableDisc: adultCruiseTaxable, childCruiseTaxableDisc: childCruiseTaxable,
      adultFoodTaxableDisc:   adultFoodTaxable,   childFoodTaxableDisc:   childFoodTaxable,
      showDiscountBlock: false,
      cruiseRate, cgstCruisePct,
      foodRate,   cgstFoodPct,
      totalCruiseCharge, totalFoodCharge,
      taxableCruise, cgstCruise, sgstCruise,
      taxableFood,   cgstFood,   sgstFood,
      taxableTotal: taxableCruise + taxableFood,
      totalTax:     cgstCruise + sgstCruise + cgstFood + sgstFood,
      grandTotal:   totalCruiseCharge + totalFoodCharge,
      // additive B2C fields (existing values above are unchanged)
      isB2B: false,
      hasDiscount: false,
      discountAmount: 0,
      fullGrandTotal: totalCruiseCharge + totalFoodCharge,
    };
  }

  // ── NON-DINNER (Model 2): per-head from `pricing` table rates, separate Adult/Child ──
  const total      = Number(booking.total_amount) || 0;
  const cruiseRate = Number(settings.cruise_gst_rate) || 18;
  const cgstPct    = cruiseRate / 2;
  const pax        = adults + children || 1;

  // Full per-head rates come from pricing; fall back to a lump split only if
  // pricing is unavailable (last resort — no adult/child distinction then).
  const lumpPerHead = total / pax;
  const adultRateFull = pricingRates && pricingRates.adult != null ? Number(pricingRates.adult) : lumpPerHead;
  const childRateFull = pricingRates && pricingRates.child != null ? Number(pricingRates.child) : lumpPerHead;

  const adultTaxableFull = adultRateFull / (1 + cruiseRate / 100);
  const childTaxableFull = childRateFull / (1 + cruiseRate / 100);
  const fullTaxable      = adults * adultTaxableFull + children * childTaxableFull;
  const fullGrandTotal   = adults * adultRateFull + children * childRateFull; // = total_amount (full)

  if (isB2B) {
    const rate   = Number(partnerContext.tacRate); // display rate for the label
    const factor = effectiveFactor;                // AMOUNT-driven, from real totals

    const adultRateDisc = adultRateFull * factor;
    const childRateDisc = childRateFull * factor;
    const adultTaxableDisc = adultRateDisc / (1 + cruiseRate / 100);
    const childTaxableDisc = childRateDisc / (1 + cruiseRate / 100);

    const taxable = adults * adultTaxableDisc + children * childTaxableDisc; // net (for GST)
    const cgst    = taxable * (cgstPct / 100);
    const sgst    = cgst;
    const grandTotal = adults * adultRateDisc + children * childRateDisc;    // net = net_total (paid)
    const discountTaxable = fullTaxable - taxable;

    return {
      isDinner: false,
      isB2B: true,
      hasDiscount,
      adults, children,
      cruiseRate, cgstPct,
      // per-head FULL taxable (item table, full rate)
      adultTaxableFull, childTaxableFull,
      // per-head discounted taxable (net)
      adultTaxable: adultTaxableDisc, childTaxable: childTaxableDisc,
      // DISCOUNTED per-head taxables (line items show discounted rate)
      adultTaxableDisc, childTaxableDisc,
      showDiscountBlock: false,
      // line totals FULL (item table)
      taxableAdultFull: adults * adultTaxableFull,
      taxableChildFull: children * childTaxableFull,
      taxableTotalFull: fullTaxable,
      // net taxable + tax (tax table + totals)
      total,
      taxable,
      taxableTotal: taxable,
      cgst, sgst,
      totalTax: cgst + sgst,
      grandTotal,
      fullGrandTotal,
      discountAmount: bookingTacAmt, // ACTUAL stored discount, not rate-derived
      discountTaxable,
      tacRate: rate,
      perPersonTaxable: taxable / pax,
    };
  }

  // B2C non-dinner: full = net (no discount).
  const taxable = fullTaxable;
  const cgst    = taxable * (cgstPct / 100);
  const sgst    = cgst;

  return {
    isDinner: false,
    isB2B: false,
    hasDiscount: false,
    adults, children,
    cruiseRate, cgstPct,
    adultTaxableFull, childTaxableFull,
    adultTaxable: adultTaxableFull, childTaxable: childTaxableFull,
    // B2C: *Disc per-head taxables = full (no discount)
    adultTaxableDisc: adultTaxableFull, childTaxableDisc: childTaxableFull,
    showDiscountBlock: false,
    taxableAdultFull: adults * adultTaxableFull,
    taxableChildFull: children * childTaxableFull,
    taxableTotalFull: fullTaxable,
    total,
    taxable,
    taxableTotal: taxable,
    cgst, sgst,
    totalTax: cgst + sgst,
    grandTotal: fullGrandTotal,
    fullGrandTotal,
    discountAmount: 0,
    discountTaxable: 0,
    perPersonTaxable: taxable / pax,
  };
};

// ── Sub-component ─────────────────────────────────────────────────────────────
function TaxRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ fontSize: '0.79rem', color: '#6b7280' }}>{label}</span>
      <span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{value}</span>
    </div>
  );
}

const f2  = (n) => `₹${Number(n || 0).toFixed(2)}`;
const fI  = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

// ── Main component ────────────────────────────────────────────────────────────
export default function InvoiceModal({ booking, existingInvoice, onClose, onGenerated }) {
  const [invoiceDate, setInvoiceDate]   = useState(existingInvoice?.invoice_date || todayInput());
  const [customerName, setCustomerName] = useState(existingInvoice?.customer_name || booking.booking_name || '');
  const [customerPhone, setCustomerPhone] = useState(existingInvoice?.customer_phone || booking.guest_phone || '');
  const [remarks, setRemarks]             = useState(existingInvoice?.remarks || '');
  const [settings, setSettings]           = useState(null);
  const [cruiseSetting, setCruiseSetting] = useState(null);
  const [pricingRates, setPricingRates]   = useState(null);
  const [partner, setPartner]             = useState(null);
  const [generating, setGenerating]       = useState(false);
  const [error, setError]                 = useState('');

  const isReprint = !!existingInvoice;

  useEffect(() => {
    supabase.from('invoice_settings').select('*').limit(1).maybeSingle().then(({ data }) => {
      setSettings(data || {});
    });
    supabase.from('cruise_settings').select('*').eq('cruise_name', booking.cruise_type).maybeSingle().then(({ data }) => {
      setCruiseSetting(data || {});
    });
  }, [booking.cruise_type]);

  // Per-head Adult/Child pricing for this cruise + deck (non-dinner Model 2).
  useEffect(() => {
    supabase.from('pricing')
      .select('passenger_type, price')
      .eq('cruise_type', booking.cruise_type)
      .eq('deck', booking.deck)
      .then(({ data }) => {
        const rows = data || [];
        const find = (t) => {
          const row = rows.find(r => (r.passenger_type || '').toLowerCase() === t);
          return row ? Number(row.price) : null;
        };
        setPricingRates({ adult: find('adult'), child: find('child') });
      });
  }, [booking.cruise_type, booking.deck]);

  // Partner (for B2B invoices) — only when the booking is attached to a partner.
  useEffect(() => {
    if (!booking.partner_id) { setPartner(null); return; }
    supabase.from('partners')
      .select('id, name, gstin, gst_legal_name, gst_address, pincode, state, state_code')
      .eq('id', booking.partner_id)
      .maybeSingle()
      .then(({ data }) => {
        setPartner(data || null);
        // Default Bill-To to the partner's legal name for a new B2B invoice.
        if (!isReprint && data && data.gstin) {
          setCustomerName(data.gst_legal_name || data.name || '');
        }
      });
  }, [booking.partner_id, isReprint]);

  // B2B when a partner with a GSTIN is attached; otherwise B2C.
  const isB2B = !!(partner && partner.gstin);
  const partnerContext = isB2B
    ? { gstin: partner.gstin, tacRate: Number(booking.tac_rate) || 0 }
    : null;

  const taxData = settings ? computeInvoiceTax(booking, settings, cruiseSetting, partnerContext, pricingRates) : null;

  const handleGenerate = async () => {
    if (!customerName.trim()) { setError('Customer name is required.'); return; }
    setGenerating(true);
    setError('');

    let invoiceNumber = existingInvoice?.invoice_number;
    // Reprints keep the stored invoice id; fresh generates capture it on insert.
    let newInvoiceId = existingInvoice?.id ?? null;

    if (!isReprint) {
      const fy = getFinancialYear(invoiceDate);

      if (isB2B) {
        // ── B2B invoice: allocate a dedicated per-day sequence + formatted number ──
        const { data: seq, error: seqErr } = await supabase
          .rpc('allocate_b2b_invoice_number', { p_invoice_date: invoiceDate });
        if (seqErr || seq == null) {
          setGenerating(false);
          setError('Failed to allocate B2B invoice number: ' + (seqErr?.message || 'no sequence returned'));
          return;
        }
        // Daily-reset number: BCPL/ONL/YYMMDD/0001 (invoiceDate is "YYYY-MM-DD")
        const [yStr, mStr, dStr] = invoiceDate.split('-');
        const yymmdd = `${yStr.slice(2)}${mStr}${dStr}`;
        invoiceNumber = `BCPL/ONL/${yymmdd}/${String(seq).padStart(4, '0')}`;

        const payload = {
          booking_id:         booking.id,
          invoice_number:     invoiceNumber,
          invoice_date:       invoiceDate,
          customer_name:      customerName,
          customer_phone:     customerPhone,
          financial_year:     fy,
          sequence_number:    seq,
          invoice_type:       'b2b',
          cruise_type:        booking.cruise_type,
          deck:               booking.deck,
          adults:             booking.adults,
          children:           booking.children,
          total_amount:       Number(booking.total_amount) || 0,
          grand_total:        taxData ? taxData.grandTotal : (Number(booking.total_amount) || 0),
          partner_id:         partner.id,
          partner_gstin:      partner.gstin,
          partner_legal_name: partner.gst_legal_name || partner.name,
          partner_address:    partner.gst_address || null,
          partner_pincode:    partner.pincode || null,
          partner_state:      partner.state || null,
          partner_state_code: partner.state_code || null,
          discount_amount:    taxData ? taxData.discountAmount : 0,
          tac_rate:           Number(booking.tac_rate) || 0,
          taxable_amount_cruise: taxData ? (taxData.isDinner ? taxData.taxableCruise : taxData.taxable) : 0,
          cgst_cruise:           taxData ? (taxData.isDinner ? taxData.cgstCruise    : taxData.cgst)    : 0,
          sgst_cruise:           taxData ? (taxData.isDinner ? taxData.sgstCruise    : taxData.sgst)    : 0,
          taxable_amount_food:   taxData ? (taxData.isDinner ? taxData.taxableFood : 0) : 0,
          cgst_food:             taxData ? (taxData.isDinner ? taxData.cgstFood    : 0) : 0,
          sgst_food:             taxData ? (taxData.isDinner ? taxData.sgstFood    : 0) : 0,
          total_tax:             taxData ? taxData.totalTax : 0,
          remarks:               remarks.trim() || null,
        };
        const { data: insertedInvoice, error: saveErr } = await supabase.from('invoices').insert(payload).select('id').single();

        setGenerating(false);
        if (saveErr) { setError('Failed to save: ' + saveErr.message); return; }
        newInvoiceId = insertedInvoice.id;
      } else {
        // ── B2C invoice: reference number as invoice number (unchanged) ──
        const { data: refRow } = await supabase
          .from('booking_references')
          .select('reference_number')
          .eq('booking_id', booking.id)
          .maybeSingle();
        invoiceNumber = refRow?.reference_number || null;
        if (!invoiceNumber) {
          setGenerating(false);
          setError('No booking reference found for this booking. Generate a reference number first.');
          return;
        }

        const grandTotal = taxData ? taxData.grandTotal : (Number(booking.total_amount) || 0);
        const payload = {
          booking_id:     booking.id,
          invoice_number: invoiceNumber,
          invoice_date:   invoiceDate,
          customer_name:  customerName,
          customer_phone: customerPhone,
          financial_year: fy,
          cruise_type:    booking.cruise_type,
          total_amount:   Number(booking.total_amount) || 0,
          grand_total:    grandTotal,
          invoice_type:   'b2c',
          remarks:        remarks.trim() || null,
        };
        const { data: insertedInvoice, error: saveErr } = await supabase.from('invoices').insert(payload).select('id').single();

        setGenerating(false);
        if (saveErr) { setError('Failed to save: ' + saveErr.message); return; }
        newInvoiceId = insertedInvoice.id;
      }
    } else {
      setGenerating(false);
    }

    onGenerated({
      id: newInvoiceId,
      invoiceNumber,
      invoiceDate,
      customerName,
      customerPhone,
      booking,
      settings,
      taxData,
      isB2B,
      // Guest-facing long name for the printed Cruise row (display only; the
      // invoice row above and booking.cruise_type keep the stored name).
      cruiseDisplayName: cruiseLabel(cruiseSetting) || booking.cruise_type,
      // On reprint use the stored remarks; on fresh generate use the typed value.
      remarks: isReprint ? (existingInvoice.remarks || '') : (remarks.trim() || ''),
      // On reprint, show the permanent record stored on the invoice row; on a
      // fresh generate, use the partner's current values.
      partner_gstin:      isReprint ? (existingInvoice.partner_gstin || null)
                                    : (partner ? partner.gstin : null),
      partner_legal_name: isReprint ? (existingInvoice.partner_legal_name || null)
                                    : (partner ? (partner.gst_legal_name || partner.name) : null),
      partner_address:    isReprint ? (existingInvoice.partner_address || null)
                                    : (partner ? (partner.gst_address || null) : null),
      partner_pincode:    isReprint ? (existingInvoice.partner_pincode || null)
                                    : (partner ? (partner.pincode || null) : null),
      partner_state:      isReprint ? (existingInvoice.partner_state || null)
                                    : (partner ? (partner.state || null) : null),
      partner_state_code: isReprint ? (existingInvoice.partner_state_code || null)
                                    : (partner ? (partner.state_code || null) : null),
    });
  };

  const inp = {
    width: '100%', padding: '8px 12px',
    border: '1.5px solid #d8d0c0', borderRadius: 8,
    fontSize: '0.88rem', outline: 'none',
    boxSizing: 'border-box', background: '#fff', color: NAVY,
  };
  const lbl = { display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#5a4a38', marginBottom: 4 };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16, overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, padding: '24px', boxShadow: '0 8px 40px rgba(10,22,40,0.22)', maxHeight: '92vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: NAVY }}>
              {isReprint ? 'Reprint Invoice' : 'Generate GST Invoice'}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#9a8e7a' }}>
              {booking.cruise_type} · {booking.deck}
            </p>
            {isReprint && (
              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', fontWeight: 700, color: GOLD }}>
                {existingInvoice.invoice_number}
              </p>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9a8e7a', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        {/* Invoice date (editable for new only) */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Invoice Date</label>
          <input
            type="date" value={invoiceDate}
            onChange={e => setInvoiceDate(e.target.value)}
            disabled={isReprint}
            style={{ ...inp, colorScheme: 'light', opacity: isReprint ? 0.6 : 1 }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Customer Name</label>
          <input value={customerName} onChange={e => setCustomerName(e.target.value)} style={inp} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Customer Phone</label>
          <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} style={inp} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Remarks (optional)</label>
          <input
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            disabled={isReprint}
            placeholder="e.g. tour code / group reference"
            style={{ ...inp, opacity: isReprint ? 0.6 : 1 }}
          />
        </div>

        {/* Booking details (read-only) */}
        <div style={{ background: '#f8f6f1', border: '1px solid #e8e0d0', borderRadius: 10, padding: '12px 16px', marginBottom: 18 }}>
          <div style={{ fontSize: '0.71rem', fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Booking</div>
          {[
            ['Cruise',    booking.cruise_type],
            ['Deck',      booking.deck],
            ['Adults',    booking.adults || 0],
            ['Children',  booking.children || 0],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: '0.8rem', color: '#7a6a50', fontWeight: 600 }}>{k}</span>
              <span style={{ fontSize: '0.8rem', color: NAVY, fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Tax breakdown preview */}
        {taxData ? (
          <div style={{ border: '1px solid #e8e0d0', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ background: NAVY, padding: '9px 14px', fontSize: '0.71rem', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Tax Breakdown Preview
            </div>
            <div style={{ padding: '14px 16px' }}>
              {taxData.isDinner ? (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a50', marginBottom: 5 }}>
                      Cruise Charges (incl. {taxData.cruiseRate}% GST)
                    </div>
                    {taxData.adults > 0 && <TaxRow label={`${taxData.adults} Adult × ₹${taxData.adultCruise}`}       value={f2(taxData.adults * taxData.adultCruise)} />}
                    {taxData.children > 0 && <TaxRow label={`${taxData.children} Child × ₹${taxData.childCruise}`}  value={f2(taxData.children * taxData.childCruise)} />}
                    <TaxRow label="  Taxable (excl. GST)"                    value={f2(taxData.taxableCruise)} />
                    <TaxRow label={`  CGST ${taxData.cgstCruisePct}%`}       value={f2(taxData.cgstCruise)} />
                    <TaxRow label={`  SGST ${taxData.cgstCruisePct}%`}       value={f2(taxData.sgstCruise)} />
                  </div>
                  <div style={{ height: 1, background: '#e8e0d0', margin: '6px 0' }} />
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a50', marginBottom: 5 }}>
                      Food & Beverage (incl. {taxData.foodRate}% GST)
                    </div>
                    {taxData.adults > 0 && <TaxRow label={`${taxData.adults} Adult × ₹${taxData.adultFood}`}       value={f2(taxData.adults * taxData.adultFood)} />}
                    {taxData.children > 0 && <TaxRow label={`${taxData.children} Child × ₹${taxData.childFood}`}  value={f2(taxData.children * taxData.childFood)} />}
                    <TaxRow label="  Taxable (excl. GST)"                  value={f2(taxData.taxableFood)} />
                    <TaxRow label={`  CGST ${taxData.cgstFoodPct}%`}       value={f2(taxData.cgstFood)} />
                    <TaxRow label={`  SGST ${taxData.cgstFoodPct}%`}       value={f2(taxData.sgstFood)} />
                  </div>
                </>
              ) : (
                <>
                  <TaxRow label={`Total (incl. ${taxData.cruiseRate}% GST)`} value={f2(taxData.total)} />
                  <TaxRow label="Taxable (excl. GST)"                        value={f2(taxData.taxable)} />
                  <TaxRow label={`CGST ${taxData.cgstPct}%`}                 value={f2(taxData.cgst)} />
                  <TaxRow label={`SGST ${taxData.cgstPct}%`}                 value={f2(taxData.sgst)} />
                </>
              )}
              <div style={{ height: 1, background: '#e8e0d0', margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY }}>Grand Total</span>
                <span style={{ fontSize: '0.88rem', fontWeight: 800, color: GOLD }}>{fI(taxData.grandTotal)}</span>
              </div>
            </div>
          </div>
        ) : (
          <p style={{ color: '#9a8e7a', fontSize: '0.82rem', marginBottom: 18 }}>Loading tax settings…</p>
        )}

        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleGenerate}
            disabled={generating || !settings}
            style={{
              flex: 1, background: NAVY, color: GOLD, border: 'none', borderRadius: 8,
              padding: '10px 0', fontSize: '0.88rem', fontWeight: 700,
              cursor: (generating || !settings) ? 'not-allowed' : 'pointer',
              opacity: (generating || !settings) ? 0.7 : 1,
            }}
          >
            {generating ? 'Generating…' : isReprint ? 'Open Invoice' : 'Generate Invoice'}
          </button>
          <button
            onClick={onClose}
            style={{ flex: 1, background: 'none', color: '#5a4a38', border: '1px solid #d8d0c0', borderRadius: 8, padding: '10px 0', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
